import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../utils/cn';
import './Accordion.scss';

/**
 * Accordion component for collapsible content
 * Custom implementation (no external dependencies)
 */

const Accordion = ({ type = 'single', collapsible = true, children, className, ...props }) => {
  const [openItems, setOpenItems] = useState([]);

  const handleToggle = value => {
    if (type === 'single') {
      // Single mode: only one item open at a time
      if (openItems.includes(value)) {
        setOpenItems(collapsible ? [] : [value]);
      } else {
        setOpenItems([value]);
      }
    } else {
      // Multiple mode: multiple items can be open
      setOpenItems(prev => (prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]));
    }
  };

  return (
    <div className={cn('accordion', className)} {...props}>
      {React.Children.map(children, child => React.cloneElement(child, { openItems, onToggle: handleToggle }))}
    </div>
  );
};

const AccordionItem = ({ value, children, openItems = [], onToggle, className }) => {
  const isOpen = openItems.includes(value);

  return (
    <div className={cn('accordion-ui__item', className)} data-state={isOpen ? 'open' : 'closed'}>
      {React.Children.map(children, child => React.cloneElement(child, { value, isOpen, onToggle }))}
    </div>
  );
};

const AccordionTrigger = ({ value, isOpen, onToggle, children, className }) => {
  return (
    <div className='accordion-ui__header'>
      <button
        type='button'
        className={cn('accordion-ui__trigger', className)}
        onClick={() => onToggle(value)}
        aria-expanded={isOpen}
      >
        {children}
        <ChevronDown className='accordion-ui__icon' />
      </button>
    </div>
  );
};

const AccordionContent = ({ isOpen, children, className }) => {
  return (
    <div className={cn('accordion-ui__content', className)} data-state={isOpen ? 'open' : 'closed'}>
      <div className='accordion-ui__content-inner'>{children}</div>
    </div>
  );
};

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
